import { Tooltip as MaterialTooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { PurePtr, useLocalStoragePtr, useStatePtr } from "@pure-ptr/react";
import { isEqual } from "lodash";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { set } from "../../store/actions/pages";
import { RootState } from "../../store/reducers";
import { CombinedFilter, getPaginationFilter, getTableSearchFilter, TimeFilter } from "../../util/pagination";
import { PopoverLink } from "../popoverLink";

export type SortOptions = { id: string; direction: string }[]
export type PaginationFilters = { sort: SortOptions, filters: CombinedFilter, pagination: { currentPage: number; recordsPerPage: number; totalRecords?: number } }

export function createActionsField( getActions, headerName = 'Actions') : GridColDef {
    return {
        field: 'actions',
        headerName,
        type: 'actions' as any,
        getActions( params ) {
            return params.id === 'totals' ? [] : getActions( params );
        }
    }
}

export function renderHoverLink( hoverData, titleSubstring = false ) {
    return (params) => (
        <PopoverLink
            columnData={params.value}
            title={titleSubstring ? `${params.value.substring(0, 15)}...` : params.value}
            hoverData={ hoverData( params.row )}
        />
    )
}

export function renderProcessTypeHoverLink(hoverData) {
    return (params) => {
        const { processType, processSubType } = params.row || {};
        if (processType && processType !== "RULE") {
            if ((processType === "OPPS" || processType === "EAPG") && processSubType) {
                return (
                    <>
                        {processType}
                        <MaterialTooltip title={processSubType}>
                            <i className={`fas fa-briefcase ml-2 ${processSubType === 'PACKAGED' ? 'text-info' : 'text-success'}` }></i>
                        </MaterialTooltip>
                    </>
                );
            }
            return processType || '';
        }

        return <PopoverLink
            columnData={params.value}
            title={params.value}
            hoverData={ hoverData( params.row )}
        />
    }
}

type TableFilter = { sort: SortOptions, filters: CombinedFilter, pagination: { currentPage: number; recordsPerPage: number; totalRecords?: number } }

/**
 * Generates a hook for retrieving grid filters, sorting options, and pagination settings for a specified page.
 *
 * @template K - The key of the page in the RootState.
 * @param {K} page - The key representing the page in the RootState.
 * @param {Object} [defaults] - Optional default values for filters and sorting options.
 * @param {any[]} [defaults.filter] - Default filter values.
 * @param {SortOptions} [defaults.sort] - Default sorting options.
 * @returns {() => { sort: SortOptions, filters: CombinedFilter, pagination: { currentPage: number; recordsPerPage: number; totalRecords?: number } }} 
 *          A hook function that returns the current sorting options, combined filters, and pagination settings.
 */
export function pageGridFilters<K extends keyof RootState['pages']>( page : K, defaults? : { filter?: any[], sort?: SortOptions }) 
    : () => TableFilter
{
    return function useGridFilters() {
        const filter = useGlobalStore( x => x.pages[page].filter ),
            customFilter = useGlobalStore( x => x.pages[page].customFilter ),
            timeFilter = useGlobalStore( x => x.pages[page].timeFilter ),
            defaultFilter = useGlobalStore( x => x.pages[page].defaultFilter ),
            defaultSort = useGlobalStore( x => x.pages[page].defaultSort ),
            defaultCustomFilter = useGlobalStore( x => x.pages[page].defaultCustomFilter ),

            searchTerm = useGlobalStore( x => x.pages[page].searchTerm ),
            sort = useGlobalStore( x => x.pages[page].sort ),
            pagination = useGlobalStore( x => x.pages[page].page );

        const quickSearch = searchTerm ? getTableSearchFilter( searchTerm ) : null;

        const paginationFilters = getPaginationFilter(
            getDefault(filter, defaults?.filter ?? defaultFilter), 
            undefined, 
            timeFilter, 
            getDefault(customFilter, defaultCustomFilter)
                .filter( ( item : any ) => item.value !== 'both') as any
        );
        
        return {
            sort : getDefault(sort, defaults?.sort ?? defaultSort),
            filters : ( quickSearch ? {
                    ...paginationFilters,
                    operands: [
                        ...(paginationFilters?.operands || []),
                        ...quickSearch?.operands,
                    ],
                }
            : paginationFilters ) as any,
            pagination
        }
    }
}

import _ from "lodash";
import { selectDefaultCustomFilters } from "../../store/selectors/claimAnalysis";
import { getRedirectFilters } from "../../pages/ClaimSummaryDashboard/Dashboard";

/**
 * Performs deep comparison between two data structures (PlainObject or Array).
 * If matching subtrees are found (even if the pointers differ), the pointer/reference from `prev` is used.
 *
 * Logic:
 * 1. If `prev` and `next` are deeply equal (using _.isEqual), return `prev`.
 * 2. For arrays:
 *    - If both are arrays of the same length, recursively process each element.
 *    - If the arrays have different lengths (or `prev` is not an array), return `next` directly.
 * 3. For plain objects:
 *    - Recursively process each property in `next` by comparing with the corresponding property in `prev`.
 * 4. For primitives or non-matching types, return `next`.
 *
 * @param prev - The previous state (object or array).
 * @param next - The new state (object or array).
 * @returns A new structure where unchanged subtrees reference those in `prev`.
 */
export function applyStructuralSharing<T>( prev: T, next: T ): T {
  // Base case: if both values are deeply equal, return prev.
  if (_.isEqual(prev, next)) {
    return prev;
  }

  // Process arrays.
  if (Array.isArray(next)) {
    // If prev is not an array or lengths differ, return next as is.
    if (!Array.isArray(prev) || (prev as any[]).length !== next.length) {
      return next;
    }
    // If both arrays have the same length, process each element recursively.
    const newArray: any[] = [];
    for (let i = 0; i < next.length; i++) {
      newArray[i] = applyStructuralSharing((prev as any[])[i], next[i]);
    }
    return newArray as unknown as T;
  }

  // Process plain objects.
  if (next && typeof next === "object") {
    if (!prev || typeof prev !== "object") {
      return next;
    }
    const newObject: { [key: string]: any } = {};
    // Iterate through the keys of next.
    for (const key of Object.keys(next)) {
      newObject[key] = applyStructuralSharing((prev as any)[key], (next as any)[key]);
    }
    return newObject as T;
  }

  // For primitives or incompatible types, return next.
  return next;
}

export const defaultTableFilterState = {
    __version: 1,
    filters :{
        filter: [],
        customFilter: [],
        timeFilter: null as TimeFilter,
        searchTerm: '',
        significance: true,
        selectedFilterId: null,
        defaultFilterId: null,
        ignoreAcknowledgement: false,
        showIgnoredFilterDialog: false,
        assigned:false
    },
    sort: [],
    pagination: {
        currentPage: 1,
        recordsPerPage: 100,
        totalRecords: 0,
    },
    isFirstTime : true,
}
export type TableFilterState = typeof defaultTableFilterState;

export function whatChanged() {
    let prev;

    return function( arr : any[] ){
        if( prev === undefined || prev.length !== arr.length ) {
            console.log( 'Initialized:', arr );
        }
        else {
            console.log( 'Shallow Changed:', arr.map( ( item, index ) => item === prev[index] ? '_' : item ) );
            console.log( 'Deep Changed', arr.map( ( item, index ) => isEqual( item, prev[index] ) ? '_' : item ) );
        }

        prev = arr;
    }
}

export function useDefaultFiltersState(){
    const defaultCustomFilter = useSelector( selectDefaultCustomFilters as any ) as any[];

    return function() {
        return {
            ...defaultTableFilterState.filters,
            customFilter: getDefault( defaultTableFilterState.filters.customFilter, defaultCustomFilter )
        }
    }
}

export function clearSessionStorage( prefix : string ) {
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);

        if( key.indexOf( prefix + ':' ) === 0 ) {
            sessionStorage.removeItem(key);
        }
    }
}

function getInitialState<S>( initialState : S | (() => S)) : S {
    return typeof initialState === 'function' ? (initialState as any)() : initialState;
}

export function useSessionStoragePtr<S>( key : string, initialState : S | (() => S), parse? : ( s : any ) => S )  : PurePtr<S> {

    const valuePtr = useStatePtr<S>( () => {
        const json = JSON.parse( sessionStorage.getItem( key ) || 'null' );

        return ( parse ? parse( json ) : json ) || getInitialState( initialState );
    });

    return valuePtr.onChange( x => {
        sessionStorage.setItem( key, JSON.stringify({ ...x }) );
    })
}

export function useTableFilter( page : string, redirect = false ) : [ TableFilter, PurePtr<TableFilterState> ] {
    const defaultFilters = useDefaultFiltersState();

    const tableFilterPtr = (
        !redirect ?
            useSessionStoragePtr( 'page-state:' + page, () => ({
                    ...defaultTableFilterState,
                    filters : defaultFilters(), 
                }), 
                json => {
                    if( !json ) return json;

                    // Reset page size once.
                    if( !json.__version || json.__version < 1 ) {
                        json.__version = 1;
                        json.pagination = {
                            ...json.pagination,
                            recordsPerPage: defaultTableFilterState.pagination.recordsPerPage,
                        };
                    }

                    return json;
                }
            ) 
        :
            useStatePtr( () => ({
                ...defaultTableFilterState,
                isFirstTime : false,
                filters : getRedirectFilters( page ),
            }) )
        )
        .pipe( ( next, prev ) => {
            // Don't update if nothing changed
            if( !isEqual( next, prev ) ) {
                // Deduplicate object parts that are the same
                const update = applyStructuralSharing( prev, next );

/*                 console.log( 'Query params changed', update );
                console.log( 'filters', update.filters === prev.filters );
                console.log( 'sort', update.sort === prev.sort );
                console.log( 'pagination', update.pagination === prev.pagination ); */

                const isFiltersChanged = !isEqual( update.filters, prev.filters );

                return isFiltersChanged ? { 
                    ...update,
                    // If the filters have changed, reset the pagination to the first page
                    pagination : { 
                        ...update.pagination, 
                        currentPage: 1 
                    }
                } 
                : update;
            }
            else {
                console.log( 'No change in query params' );
            }
        });

    const { filters, sort, pagination } = tableFilterPtr.value;

    const value = useMemo( () => {
        const quickSearch = filters.searchTerm ? getTableSearchFilter( filters.searchTerm ) : null;

        const paginationFilters = getPaginationFilter(
            filters?.filter || [], 
            undefined, 
            filters?.timeFilter, 
            (filters?.customFilter || [])
                .filter( ( item : any ) => item.value !== 'both')
        );

        return (
            filters?.searchTerm?.length > 0 ?
                filters?.ignoreAcknowledgement ? 
                    quickSearch 
                : 
                    {
                        ...( paginationFilters ?? [] ),
                        operands: [
                            ...(paginationFilters?.operands || []),
                            ...quickSearch?.operands,
                        ],
                    }
            : paginationFilters 
        ) as any
    }, [ filters.searchTerm, filters.filter, filters.customFilter, filters.timeFilter, filters?.ignoreAcknowledgement ]);

    return [
        {
            sort,
            pagination, 
            filters: value,
        },
        tableFilterPtr
    ]
}

type Initializer<T> = T | (() => T);

export function usePageStore<P extends keyof RootState['pages'], K extends keyof RootState['pages'][P]>( page : P, key : K, init? : Initializer<RootState['pages'][P][K]> ) : RootState['pages'][P][K] {
    const val = useSelector<RootState>( x => x.pages[page][key] );

    return getDefault( val, init );
}

export function usePageStorePtr<P extends keyof RootState['pages'], K extends keyof RootState['pages'][P]>( page : P, key : K, init? : Initializer<RootState['pages'][P][K]> ) : PurePtr<RootState['pages'][P][K]> {
    const dispatch = useDispatch(),
        val = useSelector<RootState>( x => x.pages[page][key] );

    return PurePtr.value( getDefault( val, init ), x => dispatch( set( page, key, x )));
}

/**
 * A utility function to create a typed selector hook for a specific page in the Redux store.
 * 
 * @template K - The key of the page in the RootState's pages object.
 * @template V - The type of the value returned by the selector function.
 * 
 * @param {K} page - The key of the page in the RootState's pages object.
 * 
 * @returns {function} - A hook function that takes a selector function and returns the selected value from the specified page's state.
 * 
 * @example
 * ```tsx
 * const useHomePageStore = pageStore('home');
 * ...
 * const someValue = useHomePageStore(x => x.someValue);
 * ```
 */
export function pageStore<K extends keyof RootState['pages']>( page : K, options? : any ) : {
    usePageStore<V>( selector : ( state : RootState['pages'][K] ) => V ) : V;
    useGridFilters() : ReturnType<ReturnType<typeof pageGridFilters>>;
    usePageStorePtr<V extends keyof RootState['pages'][K]>( key : V, init? : any ) : PurePtr<RootState['pages'][K][V]>;
}{
    return {
        usePageStore( selector : ( state : RootState['pages'][K] ) => any ){
            return useSelector<RootState, any>( x => selector(x.pages[page]) );
        },

        useGridFilters : pageGridFilters( page, options ),

        usePageStorePtr( key : any, init? : any ){
            const value = useSelector<RootState, any>( x => x.pages[page][key] );
            const dispatch = useDispatch();
            return PurePtr.value( getDefault( value, init ) , x => dispatch( set( page, key, x )));
        }
    }
}

/**
 * Creates a hook that returns a pointer to a specific value in the given Redux store's `pages` slice.
 *
 * @template K - The key of the `pages` slice in the `RootState`.
 * @template V - The key of the specific value within the `pages` slice.
 * 
 * @param {K} page - The key of the `pages` slice in the `RootState`.
 * @returns {function(V): PurePtr<RootState['pages'][K][V]>} - A function that takes a key and returns a pointer to the value in the Redux store.
 */
export function pageStorePtr<K extends keyof RootState['pages']>( page : K ) : <V extends keyof RootState['pages'][K]>( key : V, init?: any ) => PurePtr<RootState['pages'][K][V]> {
    return function usePageStorePtr( key : any, defaultVal? : any ){
        const value = useSelector<RootState, any>( x => x.pages[page][key] );
        const dispatch = useDispatch();
        return PurePtr.value( getDefault( value, defaultVal ) , x => dispatch( set( page, key, x )));
    }
}

export function useGlobalStore<T>( selector : ( state : RootState ) => T ) : T {
    return useSelector<RootState, T>( selector ?? ( x => x as any ));
}

/* TODO: Need reducer.

export function useGlobalStorePtr<K extends keyof RootState>( key : K ) : PurePtr<RootState[K]> {
    const value = useSelector<RootState, any>(  x => x[ key ] );
    const dispatch = useDispatch();
    return PurePtr.value( value, x => dispatch( x as any ));
}
*/

function getDefault<T>( arr : any, defaultArr : T | (() => T) ) : T {
    if( defaultArr === undefined ) return arr;
    
    const defaultValue = typeof defaultArr === 'function' ? ( defaultArr as any )() : defaultArr;
    
    return Array.isArray( defaultValue ) ?
        ( !arr?.length ? defaultArr : arr ) :
        arr ?? defaultArr;
}

export const dataGridStyles = {
    '& .Mui-error': {
        backgroundColor: 'rgba(255, 0, 0, 0.1)',
    },
    
    '& .MuiDataGrid-footerContainer': {
        minHeight: 36,
        '& .MuiTablePagination-toolbar': {
            minHeight: 36,
        }
    },

    '& .MuiDataGrid-columnHeaders': {
        '& [role="row"]': {
            backgroundColor: '#F5F5F5',
        }
    },

    '& .MuiDataGrid-pinnedRows': {
        '& .MuiDataGrid-row': {
            backgroundColor: '#FAFAFA',
        },

        '& .MuiDataGrid-cell': {
            fontWeight: 600,
        }
    }
}